import { Button, ConfigProvider, Modal } from 'antd';
import { t } from 'i18next';
import { ReactNode } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { store } from 'src/app/redux/store';
import { PureCancelIcon } from 'src/icons';
import { CSSProperties } from 'styled-components';
import tinycolor from 'tinycolor2';

export interface IModalProps {
  title: string;
  content: string | JSX.Element;
  okText?: string;
  size?: 'medium' | 'medium-2' | 'large' | 'small' | 'xlarge' | 'xsmall' | 'xlarge-2';
  disableButtons?: boolean;
  disableCancelButton?: boolean;
  event?: React.MouseEvent<HTMLElement>;
  onSave?: (modal: { destroy: () => void; update: (configUpdate: IModalProps) => void }) => void;
  hasFooter?: boolean;
  backgroundColor?: CSSProperties['color'];
  onCancel?: () => void;
  saveButtonDisabled?: boolean;
  footer?: ReactNode;
  isClosable?: boolean;
  cancelText?: string;
}

// This function is used to create customizable modals
const showModal = ({
  content,
  title,
  backgroundColor,
  disableCancelButton,
  event,
  footer,
  hasFooter = true,
  okText,
  cancelText,
  onCancel,
  saveButtonDisabled,
  size,
  onSave,
  isClosable = true,
}: IModalProps) => {
  // MutationObserver class is used to prevent the modal from overflowing the screen
  const observer = new MutationObserver((mutationsList) => {
    setTimeout(() => {
      const { body } = document;
      const direction = body.dir;

      const modalElement = document.querySelectorAll('.ant-modal-content');
      const pointer = document.createElement('div');
      pointer.className = 'ant-modal-not-centered';
      pointer.classList.add(direction === 'rtl' ? 'left-arrow' : 'right-arrow');

      for (const mutation of mutationsList) {
        if (mutation.addedNodes.length) {
          const lastModal = modalElement[modalElement.length - 1];

          if (lastModal) {
            if (event) {
              const rect = (event.target as HTMLElement).getBoundingClientRect(); // get some poition, scale,... properties of the item
              const height = lastModal.clientHeight;

              const width = calculateSize(size);

              if (height + rect.y > window.innerHeight) {
                // @ts-ignore

                lastModal.style.top = `${window.innerHeight - height - 20}px`;
                pointer.style.top = `${height - window.innerHeight + rect.y + 10}px`;
              } else {
                // @ts-ignore
                lastModal.style.top = `${rect.y - 20}px`;
                pointer.style.top = '4%';
              }
              if (direction === 'rtl') {
                // @ts-ignore
                lastModal.style.left = `${-1 * (window.innerWidth - width - rect.left - 20)}px`;
                if (rect.left + lastModal.clientWidth > window.innerWidth) {
                  // @ts-ignore
                  lastModal.style.left = `${-1 * (window.innerWidth - rect.right + 30)}px`;
                  pointer.className = `ant-modal-not-centered right-arrow`;
                }
              } else if (window.innerWidth - rect.right + lastModal.clientWidth + 20 > window.innerWidth) {
                // @ts-ignore
                lastModal.style.left = `${rect.left + 20}px`;
                pointer.className = `ant-modal-not-centered left-arrow`;
              } else {
                // @ts-ignore
                lastModal.style.left = `${rect.left - width - 10}px`;
              }

              // @ts-ignore

              lastModal.parentElement.style.visibility = 'visible';
              lastModal.appendChild(pointer);
              observer.disconnect(); // Stop observing after confirming the height
            }
          }
        }
      }
    }, 100);
  });

  observer.observe(document.body, { childList: true, subtree: true });

  let isLoading = false;

  const onModalSave = (modal: { destroy: () => void; update: (configUpdate: IModalProps) => void }) => {
    if (isLoading) return;
    isLoading = true;
    if (onSave) {
      onSave(modal);
    }
    setTimeout(() => {
      isLoading = false;
    }, 1000);
  };

  const getFooter = () => {
    let result: ReactNode;
    const defaultFooter = [
      <div className="ant-modal-confirm-btns" key="modal-footer">
        {disableCancelButton ? null : (
          <Button
            onClick={() => {
              if (onCancel) onCancel();
              modal.destroy();
            }}
          >
            {cancelText || t('global.cancel')}
          </Button>
        )}
        <Button
          key="submit"
          htmlType="submit"
          onClick={() => onModalSave(modal)}
          disabled={!!saveButtonDisabled}
          style={saveButtonDisabled ? { cursor: 'not-allowed' } : undefined}
        >
          {okText || t('global.save')}
        </Button>
      </div>,
    ];

    if (!hasFooter) {
      result = null;
    } else if (footer) {
      result = <div className="ant-modal-footer">{footer}</div>;
    } else {
      result = defaultFooter;
    }
    return result;
  };

  const calculateSize = (modalSize: string | undefined) => {
    switch (modalSize) {
      case 'xlarge-2':
        return 1200;
      case 'xlarge':
        return 900;
      case 'large':
        return 800;
      case 'medium-2':
        return 700;
      case 'medium':
        return 600;
      case 'small':
        return 500;
      case 'xsmall':
        return 350;
      default:
        return 500;
    }
  };

  const modal = Modal.confirm({
    title,
    content,
    icon: null,
    closable: isClosable,
    width: calculateSize(size),
    centered: !event,
    style: {
      visibility: event ? 'hidden' : 'visible',
      fontFamily: store.getState().configContent.fontFamily,
    },
    closeIcon: <PureCancelIcon style={{ height: '18px', width: '18px' }} />,
    maskStyle: { position: 'relative' },
    wrapClassName: 'showModalComponent',
    footer: getFooter(),
    modalRender(node) {
      // Wrapping modal with providers to access global states
      return (
        <Provider store={store}>
          <BrowserRouter>
            <ConfigProvider
              theme={{
                token: {
                  fontFamily: store.getState().configContent.fontFamily,
                },
              }}
            >
              {node}
            </ConfigProvider>
          </BrowserRouter>
        </Provider>
      );
    },
    bodyStyle: {
      backgroundColor: backgroundColor || 'rgb(26, 52, 65)',
    },
    styles: {
      content: {
        color: backgroundColor ? (tinycolor(backgroundColor).isDark() ? '#fff' : '#000') : 'whitesmoke',
      },
      mask: {
        backdropFilter: 'brightness(60%)',
      },
    },
    onCancel,
    getContainer: () => document.querySelector('.fullscreen-enabled') || document.body,
  });

  return modal;
};

export default showModal;
